import React, { useEffect } from 'react';
import { StyleProp, StyleSheet, TextStyle } from 'react-native';
import { DNABox, DNAButton, DNAButtonProps, DNAText } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import { DNAChip, DNAChipProps, useCurrentPage } from 'src/components/DNA';
import { useFilterMap } from './FiltersSidebar';

// [UI2-TODO] - We should try to avoid individual nested component styling
//            - Technically DNAButton should have the correct styles to apply, we'd just
//              need to expose it correctly (some other libraries have a small context)
const styles = StyleSheet.create({
  activePublisherText: {
    color: colors['color-text-white'],
  },
  activeLibraryText: {
    color: colors['color-text-white'],
  },
  disabledPublisherText: {
    color: colors['color-black'],
  },
  disabledLibraryText: {
    color: colors['color-text-primary'],
  },
})

export enum VariantOptions {
  publisher = 'publisher',
  library = 'library',
}

type VariantOption = keyof typeof VariantOptions

enum InteractivityStates {
  active,
  disabled
}
type InteractivityState = keyof typeof InteractivityStates

interface FilterComponentProps {
  filterButton: DNAButtonProps,
}
interface TextComponentStyle {
  text: StyleProp<TextStyle>
}
interface ChipComponentProps {
  documentCountChip: Pick<DNAChipProps, 'appearance'|'status'>
}

type ComponentConfigs = FilterComponentProps & TextComponentStyle & ChipComponentProps

type VariantConfigs = Record<VariantOption, ComponentConfigs>

type InteractivityConfigs = Record<InteractivityState, VariantConfigs>

// [UI2] - Not sure why the styling is different between MSL and Publisher views? Probably remove it at some point
const interactivityConfigs: InteractivityConfigs = {
  active: {
    publisher: {
      filterButton: {
        status: 'primary',
        appearance: 'filled',
      },
      text: styles.activePublisherText,
      documentCountChip: {
        status: 'primary',
        appearance: 'filled',
      },
    },
    library: {
      filterButton: {
        status: 'primary',
        appearance: 'filled',
      },
      text: styles.activeLibraryText,
      documentCountChip: {
        status: 'primary',
        appearance: 'filled',
      },
    },
  },
  disabled: {
    publisher: {
      filterButton: {
        status: 'secondary',
        appearance: 'outline',
      },
      text: styles.disabledLibraryText,
      documentCountChip: {
        status: 'primary',
        appearance: 'filled',
      },
    },
    library: {
      filterButton: {
        status: 'secondary',
        appearance: 'outline',
      },
      text: styles.disabledLibraryText,
      documentCountChip: {
        status: 'primary',
        appearance: 'filled',
      },
    },
  },
}

interface FilterButtonProps {
  setFiltersVisible: (visible: boolean) => void
  filtersVisible: boolean,
  variant: VariantOption;
}

export const FilterButton : React.FC<FilterButtonProps> = ({ filtersVisible, setFiltersVisible, variant }) => {
  const { totalSelected } = useFilterMap()
  const route = useCurrentPage({ exact: false });
  const isPublisher = route?.configOptions?.modules?.includes('publisher');
  const isSearchView = route?.UID === 'search' || route?.UID === 'publisher-search';

  const handleFilterVisibilityToggle = () => {
    analytics?.track('LIBRARY_TOGGLE_FILTER', {
      action: 'FILTER',
      category: isPublisher
        ? isSearchView ? 'PUBLISHER_SEARCH_RESULTS' : 'PUBLISHER'
        : isSearchView ? 'VIEWER_SEARCH_RESULTS' : 'VIEWER_LIBRARY',
      context: isPublisher ? 'PUBLISHER' : 'VIEWER',
      state: filtersVisible ? 'VISIBLE' : 'HIDDEN',
    })
  }
  useEffect(handleFilterVisibilityToggle, [filtersVisible])

  const currentInteractivity: InteractivityState = filtersVisible
    ? 'active'
    : 'disabled'

  const { filterButton, text, documentCountChip } = interactivityConfigs[currentInteractivity][variant]

  const toggleFilterVisibility = () => {
    setFiltersVisible(!filtersVisible)
  }

  return (
    <DNAButton
      appearance={filterButton.appearance}
      status={filterButton.status}
      iconLeft="funnel-simple"
      onPress={toggleFilterVisibility}
    >
      <DNABox testID="filters-header-button" alignY="center">
        <DNAText style={text}>Filters</DNAText>
        <DNAChip.Beacon
          appearance={documentCountChip.appearance}
          status={documentCountChip.status}
          style={{ marginLeft: 8 }}
          children={`${totalSelected}`}
        />
      </DNABox>
    </DNAButton>
  )
}

export default FilterButton;
